import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../services/api';
import Swal from 'sweetalert2';
import { Container, Row, Col, Button, Spinner, Badge } from 'react-bootstrap';
import { Icon } from '@iconify/react';

const GuestReschedule = () => {
    // --- STATE MANAGEMENT ---
    const { token } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [step, setStep] = useState(1);
    const [selectedDate, setSelectedDate] = useState('');
    const [doctors, setDoctors] = useState([]);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [loadingSlots, setLoadingSlots] = useState(false);

    // --- DATA FETCHING ---
    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await api.get(`/guest/reschedule/${token}`);
                setData(res.data);
                setSelectedDoctor(res.data.doctor);

                const clinicId = res.data.clinic?._id;
                if (clinicId) {
                    const docRes = await api.get(`/public/doctors?clinicId=${clinicId}`);
                    setDoctors(docRes.data);
                }
            } catch (error) {
                Swal.fire({
                    title: 'Link Expired',
                    text: 'This rescheduling link is no longer valid.',
                    icon: 'warning',
                    confirmButtonColor: '#3b82f6'
                });
                navigate('/');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [token, navigate]);

    useEffect(() => {
        if (!selectedDate || !selectedDoctor) return;
        const fetchSlots = async () => {
            setLoadingSlots(true);
            try {
                const res = await api.get(`/public/v2/doctors/${selectedDoctor._id}/slots`, {
                    params: { date: selectedDate }
                });
                setAvailableSlots(res.data);
            } catch (error) {
                setAvailableSlots([]);
            } finally {
                setLoadingSlots(false);
            }
        };
        fetchSlots();
    }, [selectedDate, selectedDoctor]);

    // --- SUBMISSION HANDLER ---
    const handleConfirm = async () => {
        if (!selectedDate || !selectedSlot) {
            return Swal.fire('Incomplete', 'Please select a date and time slot.', 'info');
        }
        try {
            await api.post('/guest/reschedule/confirm', {
                token,
                newDate: selectedDate,
                newTimeSlot: selectedSlot,
                doctorId: selectedDoctor._id
            });
            await Swal.fire({
                title: 'Rescheduled!',
                text: 'Your new appointment is confirmed.',
                icon: 'success',
                confirmButtonColor: '#10b981'
            });
            navigate('/');
        } catch (error) {
            Swal.fire('Error', 'Could not reschedule. Please try again.', 'error');
        }
    };

    const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);

    // --- RENDER LOADING ---
    if (loading) return (
        <div className="vh-100 d-flex flex-column align-items-center justify-content-center bg-light">
            <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
            <p className="mt-3 fw-bold text-secondary">Loading Portal...</p>
        </div>
    );
    if (!data) return null;

    // --- MAIN DASHBOARD UI ---
    return (
        <div className="dashboard-container">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

                :root {
                    --primary: #4f46e5;
                    --primary-dark: #4338ca;
                    --bg-body: #f1f5f9;
                    --bg-panel: #ffffff;
                    --text-main: #1e293b;
                    --text-muted: #64748b;
                    --border-color: #e2e8f0;
                    --danger-bg: #fef2f2;
                    --danger-text: #991b1b;
                    --radius-lg: 16px;
                    --radius-md: 12px;
                }

                .dashboard-container {
                    background-color: var(--bg-body);
                    min-height: 100vh;
                    font-family: 'Inter', sans-serif;
                    padding: 40px 20px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }

                .dashboard-card {
                    background: var(--bg-panel);
                    border-radius: 24px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.08);
                    overflow: hidden;
                    width: 100%;
                    max-width: 1200px;
                    border: 1px solid var(--border-color);
                }

                /* LEFT SIDEBAR STYLING */
                .sidebar-panel {
                    background-color: #f8fafc;
                    border-right: 1px solid var(--border-color);
                    padding: 40px;
                    height: 100%;
                }

                .notification-box {
                    background-color: #fff1f2; /* Light Red */
                    border: 1px solid #fecdd3;
                    border-left: 4px solid #f43f5e;
                    border-radius: var(--radius-md);
                    padding: 20px;
                    margin-bottom: 30px;
                }

                .profile-snapshot {
                    background: white;
                    border: 1px solid var(--border-color);
                    border-radius: var(--radius-lg);
                    padding: 24px;
                }

                /* RIGHT CONTENT STYLING */
                .content-panel {
                    padding: 40px;
                    background: white;
                }

                /* STEPS PROGRESS */
                .step-indicator {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 6px 12px;
                    background: #eff6ff;
                    color: var(--primary);
                    border-radius: 20px;
                    font-size: 0.85rem;
                    font-weight: 600;
                    margin-bottom: 24px;
                }

                /* DOCTOR LIST ITEMS */
                .doctor-list-item {
                    display: flex;
                    align-items: center;
                    padding: 20px;
                    border: 2px solid var(--border-color);
                    border-radius: var(--radius-lg);
                    cursor: pointer;
                    transition: all 0.2s ease;
                    background: white;
                    margin-bottom: 16px;
                }
                .doctor-list-item:hover {
                    border-color: #cbd5e1;
                    background: #f8fafc;
                }
                .doctor-list-item.active {
                    border-color: var(--primary);
                    background: #eef2ff;
                    box-shadow: 0 4px 6px -1px rgba(79, 70, 229, 0.1);
                }

                /* SLOT GRID */
                .time-slot-btn {
                    border: 1px solid var(--border-color);
                    background: white;
                    color: var(--text-main);
                    padding: 12px 0;
                    border-radius: 8px;
                    font-weight: 500;
                    transition: all 0.2s;
                    width: 100%;
                }
                .time-slot-btn:hover {
                    border-color: var(--primary);
                    color: var(--primary);
                }
                .time-slot-btn.active {
                    background: var(--primary);
                    color: white;
                    border-color: var(--primary);
                    font-weight: 600;
                    box-shadow: 0 4px 6px -1px rgba(79, 70, 229, 0.3);
                }

                /* DATE INPUT OVERRIDE */
                .styled-date-input {
                    padding: 15px;
                    border: 2px solid var(--border-color);
                    border-radius: var(--radius-md);
                    width: 100%;
                    font-size: 1rem;
                    color: var(--text-main);
                    outline: none;
                    font-family: inherit;
                    transition: border 0.2s;
                }
                .styled-date-input:focus {
                    border-color: var(--primary);
                }
            `}</style>

            <div className="dashboard-card">
                <Row className="g-0">

                    {/* --- LEFT SIDEBAR: CONTEXT & NOTIFICATION --- */}
                    <Col lg={4} className="sidebar-panel">
                        {/* BRANDING */}
                        <div className="d-flex align-items-center gap-3 mb-5">
                            <div className="bg-white p-2 border rounded-3 shadow-sm text-primary">
                                <Icon icon="solar:hospital-bold-duotone" width="32" />
                            </div>
                            <div>
                                <h5 className="fw-bold text-dark mb-0 lh-1">{data.clinic.name}</h5>
                                <small className="text-muted fw-medium">Patient Portal</small>
                            </div>
                        </div>

                        {/* CRITICAL NOTIFICATION */}
                        <div className="notification-box">
                            <div className="d-flex gap-3">
                                <Icon icon="solar:danger-triangle-bold" className="text-danger flex-shrink-0 mt-1" width="24" />
                                <div>
                                    <h6 className="fw-bold text-danger mb-1">Action Required</h6>
                                    <p className="small text-dark mb-0 lh-sm opacity-75">
                                        Your appointment on <strong>{new Date(data.appointment.date).toLocaleDateString()}</strong> has been cancelled due to <strong>doctor unavailability</strong>. Please reschedule below.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* PATIENT PROFILE CARD */}
                        <div className="profile-snapshot">
                            <label className="text-uppercase small fw-bold text-muted mb-3 d-block tracking-wider">Patient Profile</label>

                            <div className="d-flex align-items-center gap-3 mb-3">
                                <div className="bg-light rounded-circle p-3 text-secondary border">
                                    <Icon icon="solar:user-bold" width="24" />
                                </div>
                                <div>
                                    <h6 className="fw-bold mb-0 text-dark">{data.appointment.guestName}</h6>
                                    <span className="small text-muted">Guest User</span>
                                </div>
                            </div>

                            <div className="d-flex align-items-center gap-2 text-muted small mb-3">
                                <Icon icon="solar:letter-linear" /> {data.appointment.guestEmail}
                            </div>

                            <div className="d-flex align-items-center gap-2 text-muted small">
                                <Icon icon="solar:phone-linear" /> {data.appointment.guestPhone || 'No phone provided'}
                            </div>

                            <hr className="my-4 border-light" />

                            <div className="d-flex justify-content-between align-items-center">
                                <span className="small fw-bold text-muted">Original ID</span>
                                <Badge bg="light" text="dark" className="border">#{data.appointment._id.slice(-6).toUpperCase()}</Badge>
                            </div>
                        </div>

                        <div className="mt-5 text-center">
                            <p className="small text-muted mb-1">Need immediate assistance?</p>
                            <a href={`tel:${data.clinic.contactNumber}`} className="fw-bold text-primary text-decoration-none">
                                <Icon icon="solar:phone-calling-bold" className="me-1" />
                                Call Clinic Support
                            </a>
                        </div>
                    </Col>

                    {/* --- RIGHT PANEL: ACTION WORKFLOW --- */}
                    <Col lg={8} className="content-panel">

                        {/* HEADER OF CONTENT */}
                        <div className="d-flex justify-content-between align-items-end mb-5 border-bottom pb-4">
                            <div>
                                <h2 className="fw-800 text-dark mb-2">Reschedule Appointment</h2>
                                <p className="text-muted mb-0">Select a new doctor and time slot that works for you.</p>
                            </div>
                            <div className="text-end d-none d-md-block">
                                <span className="d-block small fw-bold text-muted mb-1">Current Status</span>
                                <Badge bg="warning" text="dark" className="px-3 py-2 rounded-pill">Pending Selection</Badge>
                            </div>
                        </div>

                        {/* STEP 1: DOCTOR SELECTION */}
                        {step === 1 && (
                            <div className="animate__animated animate__fadeIn">
                                <div className="step-indicator">
                                    <span className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: 20, height: 20 }}>1</span>
                                    Select Specialist
                                </div>

                                <div className="doctor-list-container">
                                    {doctors.map((doc) => (
                                        <div
                                            key={doc._id}
                                            className={`doctor-list-item ${selectedDoctor?._id === doc._id ? 'active' : ''}`}
                                            onClick={() => setSelectedDoctor(doc)}
                                        >
                                            {/* Avatar */}
                                            <div className="me-4 position-relative">
                                                <div className="rounded-circle overflow-hidden bg-light border d-flex align-items-center justify-content-center" style={{ width: 64, height: 64 }}>
                                                    {doc.profileImage ? (
                                                        <img src={doc.profileImage} alt={doc.name} className="w-100 h-100 object-fit-cover" />
                                                    ) : (
                                                        <span className="fw-bold text-secondary">{getInitials(doc.name)}</span>
                                                    )}
                                                </div>
                                                {selectedDoctor?._id === doc._id && (
                                                    <div className="position-absolute top-0 end-0 bg-primary border border-white rounded-circle p-1">
                                                        <Icon icon="solar:check-read-bold" className="text-white d-block" width="12" />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Info */}
                                            <div className="flex-grow-1">
                                                <h5 className="fw-bold text-dark mb-1">{doc.name}</h5>
                                                <div className="d-flex align-items-center gap-3">
                                                    <span className="badge bg-primary bg-opacity-10 text-primary border-0 rounded-1 px-2">
                                                        {doc.specialization}
                                                    </span>
                                                    <span className="small text-muted fw-medium">
                                                        <Icon icon="solar:star-bold" className="text-warning me-1" />
                                                        4.9 Rating
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Action */}
                                            <div className="ps-3 border-start">
                                                <Icon
                                                    icon={selectedDoctor?._id === doc._id ? "solar:radio-minimalistic-bold" : "solar:radio-minimalistic-linear"}
                                                    className={`fs-4 ${selectedDoctor?._id === doc._id ? 'text-primary' : 'text-muted'}`}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-4 text-end">
                                    <Button
                                        variant="primary"
                                        size="lg"
                                        className="px-5 rounded-pill fw-bold"
                                        disabled={!selectedDoctor}
                                        onClick={() => setStep(2)}
                                    >
                                        Next: Select Time <Icon icon="solar:arrow-right-linear" className="ms-2" />
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* STEP 2: DATE & SLOT */}
                        {step === 2 && (
                            <div className="animate__animated animate__fadeIn">
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                    <div className="step-indicator mb-0">
                                        <span className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: 20, height: 20 }}>2</span>
                                        Date & Time
                                    </div>
                                    <Button variant="link" className="text-decoration-none text-muted fw-bold small p-0" onClick={() => setStep(1)}>
                                        Change Doctor
                                    </Button>
                                </div>

                                <div className="mb-5">
                                    <label className="fw-bold text-dark small text-uppercase mb-2">1. Pick a Date</label>
                                    <div className="position-relative">
                                        <input
                                            type="date"
                                            className="styled-date-input"
                                            min={new Date().toISOString().split('T')[0]}
                                            value={selectedDate}
                                            onChange={(e) => { setSelectedDate(e.target.value); setSelectedSlot(null); }}
                                        />
                                        <Icon icon="solar:calendar-bold" className="position-absolute end-0 top-50 translate-middle-y me-3 text-secondary fs-5" style={{ pointerEvents: 'none' }} />
                                    </div>
                                </div>

                                <div>
                                    <label className="fw-bold text-dark small text-uppercase mb-3">2. Available Slots</label>

                                    {!selectedDate ? (
                                        <div className="text-center p-5 border border-dashed rounded-3 bg-light">
                                            <p className="text-muted fw-medium mb-0">Please select a date first</p>
                                        </div>
                                    ) : loadingSlots ? (
                                        <div className="text-center py-5">
                                            <Spinner animation="grow" size="sm" variant="primary" />
                                            <p className="text-muted small mt-2">Checking availability...</p>
                                        </div>
                                    ) : availableSlots.length === 0 ? (
                                        <div className="alert alert-warning border-warning d-flex align-items-center gap-3">
                                            <Icon icon="solar:close-circle-bold" className="fs-4" />
                                            <div>
                                                <strong>No slots available.</strong>
                                                <div className="small">Please try selecting a different date.</div>
                                            </div>
                                        </div>
                                    ) : (
                                        <Row className="g-3">
                                            {availableSlots.map((slot) => (
                                                <Col xs={4} sm={3} md={3} lg={2} key={slot}>
                                                    <button
                                                        className={`time-slot-btn ${selectedSlot === slot ? 'active' : ''}`}
                                                        onClick={() => setSelectedSlot(slot)}
                                                    >
                                                        <div style={{ textAlign: "center", width: "100%" }}>
                                                            {slot}
                                                        </div>
                                                    </button>
                                                </Col>
                                            ))}
                                        </Row>
                                    )}
                                </div>

                                <div className="mt-5 pt-4 border-top d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center gap-2">
                                        <Icon icon="solar:shield-check-bold" className="text-success" />
                                        <span className="small text-muted fw-medium">Secure Booking</span>
                                    </div>
                                    <Button
                                        variant="primary"
                                        size="lg"
                                        className="px-5 rounded-pill fw-bold shadow-sm"
                                        disabled={!selectedDate || !selectedSlot}
                                        onClick={handleConfirm}
                                    >
                                        Confirm Reschedule
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default GuestReschedule;